/**
 * Typed API client for the admin email template editor.
 *
 * Mirrors the pattern in admin.service.ts: uses authService.getApi() for
 * authenticated Axios, exposes typed request/response interfaces, no
 * global state.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

// ── Response types ────────────────────────────────────────────────────────

export interface EmailTemplateSummary {
  code: string;
  description: string | null;
  is_layout: boolean;
  is_active: boolean;
  version: number;
  has_pending_draft: boolean;
  updated_at: string;
  updated_by_name: string | null;
}

export interface EmailTemplateDetail extends EmailTemplateSummary {
  id: string;
  parent_id: string | null;
  is_draft: boolean;
  subject_tpl: string | null;
  html_body: string;
  default_vars: Record<string, unknown>;
  draft_id: string | null;
}

export interface EmailTemplateVersionRef {
  id: string;
  version: number;
  is_active: boolean;
  is_draft: boolean;
  updated_at: string;
  updated_by_name: string | null;
}

export interface PreviewResponse {
  subject: string;
  html_body: string;
  warnings: string[];
}

// ── Request body types ─────────────────────────────────────────────────────

export interface DraftCreateBody {
  subject_tpl?: string | null;
  html_body?: string | null;
  default_vars?: Record<string, unknown> | null;
  description?: string | null;
  reason: string;
  actor_password: string;
}

export interface PublishBody {
  draft_id: string;
  reason: string;
  actor_password: string;
}

export interface DiscardBody {
  reason: string;
}

export interface PreviewBody {
  ctx_vars: Record<string, unknown>;
  render_target: 'live' | 'draft';
}

// ── Service class ─────────────────────────────────────────────────────────

class EmailTemplatesService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  private readonly base = '/api/v1/admin/email-templates';

  /** List all live templates (one per code, drafts excluded from list). */
  async listTemplates(): Promise<EmailTemplateSummary[]> {
    const res = await this.api.get<EmailTemplateSummary[]>(this.base);
    return res.data;
  }

  /** Full detail of the live row for `code` (+ draft_id if draft exists). */
  async getTemplate(code: string): Promise<EmailTemplateDetail> {
    const res = await this.api.get<EmailTemplateDetail>(`${this.base}/${code}`);
    return res.data;
  }

  /** All versions for `code`, newest first. */
  async listVersions(code: string): Promise<EmailTemplateVersionRef[]> {
    const res = await this.api.get<EmailTemplateVersionRef[]>(
      `${this.base}/${code}/versions`,
    );
    return res.data;
  }

  /** Read-only detail for a specific historical version by UUID. */
  async getVersion(versionId: string): Promise<EmailTemplateDetail> {
    const res = await this.api.get<EmailTemplateDetail>(
      `${this.base}/versions/${versionId}`,
    );
    return res.data;
  }

  /**
   * Fork the live row into a new draft.
   * Only changed fields need to be supplied — unset fields inherit from live.
   */
  async createDraft(code: string, body: DraftCreateBody): Promise<EmailTemplateDetail> {
    const res = await this.api.post<EmailTemplateDetail>(
      `${this.base}/${code}/draft`,
      body,
    );
    return res.data;
  }

  /** Atomically promote a draft to live. Flushes the server-side cache. */
  async publishDraft(code: string, body: PublishBody): Promise<EmailTemplateDetail> {
    const res = await this.api.post<EmailTemplateDetail>(
      `${this.base}/${code}/publish`,
      body,
    );
    return res.data;
  }

  /** Hard-delete a draft. The discard is audit-logged server-side. */
  async discardDraft(draftId: string, body: DiscardBody): Promise<void> {
    await this.api.delete(`${this.base}/drafts/${draftId}`, { data: body });
  }

  /** Render a template version with caller-supplied context variables. */
  async preview(code: string, body: PreviewBody): Promise<PreviewResponse> {
    const res = await this.api.post<PreviewResponse>(
      `${this.base}/${code}/preview`,
      body,
    );
    return res.data;
  }
}

export default new EmailTemplatesService();
